import {MouseEvent, useEffect, useMemo, useState} from 'react';

import {CUSTOMER_AND_ABOVE_ROLES} from '../../../shared/constants';
import {checkAuthorities} from '../../../shared/helpers/checkAuthorities';
import {TypeAccountRoles} from '../../../shared/types/user-account.types';
import {getNotificationById} from '../helpers/filter-notifications';
import {useNotificationsMenu} from '../hooks/use-notifications-menu';
import {useNotificationsModel} from '../hooks/use-notifications-model';
import {BulkActionsMenu} from './notification-actions-menu/bulk-actions-menu';
import {NotificationActionsMenu} from './notification-actions-menu/notification-actions-menu';
import {NotificationsPopover} from './notifications-popover';
import {NotificationsTrigger} from './notifications-trigger';

interface NotificationsProps {
  roles: TypeAccountRoles[];
}

const NotificationsContent = () => {
  const menu = useNotificationsMenu();
  const notifications = useNotificationsModel();
  const [viewMode, setViewMode] = useState<'list' | 'details'>('list');
  const [detailsNotificationId, setDetailsNotificationId] = useState<string | null>(null);

  const selectedNotification = useMemo(
    () => getNotificationById(notifications.notifications, menu.selectedNotificationId) ?? null,
    [menu.selectedNotificationId, notifications.notifications],
  );
  const detailsNotification = useMemo(
    () => getNotificationById(notifications.notifications, detailsNotificationId) ?? null,
    [detailsNotificationId, notifications.notifications],
  );

  const handleOpenPopover = (event: MouseEvent<HTMLElement>) => {
    setViewMode('list');
    setDetailsNotificationId(null);
    notifications.refresh();
    menu.openPopover(event);
  };

  const handleClosePopover = () => {
    setViewMode('list');
    setDetailsNotificationId(null);
    menu.closePopover();
  };

  const handleOpenNotificationDetails = (id: string) => {
    setDetailsNotificationId(id);
    setViewMode('details');
  };

  const handleBackToList = () => {
    setDetailsNotificationId(null);
    setViewMode('list');
  };

  const handleMarkNotification = async (markType: 'NEW' | 'READ' | 'DELETE') => {
    if (!selectedNotification) {
      return;
    }

    const isUpdated = await notifications.updateNotification(selectedNotification.id, markType);

    if (isUpdated) {
      menu.closeNotificationActions();
    }
  };

  const handleMarkAllAsRead = async () => {
    const isUpdated = await notifications.markAllAsRead();

    if (isUpdated) {
      menu.closeBulkActions();
    }
  };

  const handleDeleteAll = async () => {
    const isUpdated = await notifications.deleteAllNotifications();

    if (isUpdated) {
      menu.closeBulkActions();
    }
  };

  useEffect(() => {
    if (viewMode === 'details' && (!detailsNotification || detailsNotification.markType === 'DELETE')) {
      setViewMode('list');
      setDetailsNotificationId(null);
    }
  }, [viewMode, detailsNotification]);

  return (
    <>
      <NotificationsTrigger onOpen={handleOpenPopover} newNotificationsCount={notifications.newNotificationsCount} />
      <NotificationsPopover
        anchorEl={menu.popoverAnchor}
        open={menu.isPopoverOpen}
        onClose={handleClosePopover}
        onOpenBulkActions={menu.openBulkActions}
        onOpenNotificationActions={menu.openNotificationActions}
        selectedTab={notifications.selectedTab}
        newNotificationsCount={notifications.newNotificationsCount}
        notifications={notifications.visibleNotifications}
        loading={notifications.loading}
        error={notifications.error}
        onSelectTab={notifications.setSelectedTab}
        onRetry={notifications.refresh}
        viewMode={viewMode}
        selectedNotification={detailsNotification}
        onOpenNotificationDetails={handleOpenNotificationDetails}
        onBackToList={handleBackToList}
      />
      <BulkActionsMenu
        anchorEl={menu.bulkActionsAnchor}
        open={menu.isBulkActionsOpen}
        loading={notifications.patchLoading}
        canMarkAllAsRead={notifications.canMarkAllAsRead}
        hasNotifications={notifications.hasNotifications}
        onMarkAllAsRead={handleMarkAllAsRead}
        onDeleteAll={handleDeleteAll}
        onClose={menu.closeBulkActions}
      />
      <NotificationActionsMenu
        anchorEl={menu.notificationActionsAnchor}
        open={menu.isNotificationActionsOpen}
        selectedNotification={selectedNotification}
        loading={notifications.patchLoading}
        onMarkNotification={handleMarkNotification}
        onClose={menu.closeNotificationActions}
        onExited={menu.resetSelectedNotification}
      />
    </>
  );
};

export const Notifications = ({roles}: NotificationsProps) => {
  const canViewNotifications = checkAuthorities(CUSTOMER_AND_ABOVE_ROLES, roles);

  if (!canViewNotifications) {
    return null;
  }

  return <NotificationsContent />;
};
